/**
 * Scheduled Smiles Theme Configuration
 *
 * Centralized theme constants for consistent styling across the application.
 *
 * Color architecture:
 * - Indigo: DOMINATES — brand, CTAs, primary actions, headings
 * - Teal: SUPPORTS — scheduling-specific functional UI (badges, step indicators, status)
 * - Amber: PUNCTUATES — 1-2 uses per page, warm highlights, roadmap badges
 * - Heart violet: MARKS — logo heart, brand mark only
 *
 * Usage rules:
 * - NEVER hardcode hex values in components. Import from this file.
 * - Use `semanticColors` for Mantine `c=` props and inline styles.
 * - Use `brandColors` for Mantine `color=` props (palette names).
 * - Use CSS variables (var(--ss-*)) in CSS modules.
 * - Teal and indigo must never appear within ~80px of each other.
 * - Amber text must use the accessible shade (amber.7 / #B45309) — lighter
 *   amber fails WCAG AA on light backgrounds.
 *
 * @see .cursor/rules/no-hardcoded-colors.mdc for enforcement rules
 */

import { createTheme, rem, v8CssVariablesResolver, type MantineColorsTuple } from '@mantine/core';

/** Preserves Mantine v8 transparent `variant="light"` colors on Mantine 9. */
export const appCssVariablesResolver = v8CssVariablesResolver;

// ---------------------------------------------------------------------------
// Custom color scales (Tailwind-based, shade 6 = primary usage)
// ---------------------------------------------------------------------------

/**
 * Custom indigo scale — overrides Mantine's built-in indigo.
 * Shade 6 (#4F46E5) is the primary brand color used for buttons and CTAs.
 */
const ssIndigo: MantineColorsTuple = [
  '#EEF2FF', // 0 — hero glow bg, lightest tint
  '#E0E7FF', // 1 — accent box bg, hero glow stronger
  '#C7D2FE', // 2 — accent borders
  '#A5B4FC', // 3 — hover tints
  '#818CF8', // 4 — lighter interactive
  '#6366F1', // 5 — secondary interactive
  '#4F46E5', // 6 — PRIMARY: buttons, CTAs, links
  '#4338CA', // 7 — hover state
  '#3730A3', // 8 — active/pressed state
  '#312E81', // 9 — darkest, rarely used
];

/**
 * Custom teal scale — overrides Mantine's built-in teal.
 * Used exclusively for scheduling-related UI: badges, step numbers,
 * confirmation states. Conveys "it's handled" / "scheduled."
 */
const ssTeal: MantineColorsTuple = [
  '#F0FDFA', // 0 — scheduling section bg tint
  '#CCFBF1', // 1 — scheduling light bg
  '#99F6E4', // 2 — scheduling borders
  '#5EEAD4', // 3
  '#2DD4BF', // 4
  '#14B8A6', // 5
  '#0D9488', // 6 — PRIMARY scheduling accent
  '#0F766E', // 7 — scheduling badge text
  '#115E59', // 8
  '#134E4A', // 9
];

/**
 * Custom amber scale — added as new palette (Mantine has no built-in amber).
 * Used sparingly for warm highlights, roadmap pills, and the brand tagline
 * in the hero rotator. Shade 7+ required for text (WCAG AA compliance).
 */
const ssAmber: MantineColorsTuple = [
  '#FFFBEB', // 0
  '#FEF3C7', // 1
  '#FDE68A', // 2
  '#FCD34D', // 3
  '#FBBF24', // 4
  '#F59E0B', // 5 — decorative only (fails AA on light bg)
  '#D97706', // 6 — large display text only
  '#B45309', // 7 — ACCESSIBLE text amber (5.8:1 on white)
  '#92400E', // 8
  '#78350F', // 9
];

// ---------------------------------------------------------------------------
// Brand colors — Mantine palette names for `color=` props
// ---------------------------------------------------------------------------

/**
 * Brand color palette — Mantine palette names.
 *
 * Use these for Mantine component `color` props (Button, Badge, ThemeIcon, etc.)
 * where the prop expects a palette name, not a hex value.
 */
export const brandColors = {
  /** Primary brand color — buttons, links, primary actions */
  primary: 'indigo',

  /** Scheduling-specific accent — badges, step indicators, "scheduled" status */
  scheduling: 'teal',

  /** Warm highlight accent — roadmap pills, decorative badges */
  warmAccent: 'amber',

  /** Success/confirmation — notifications, success alerts */
  success: 'green',

  /** Warning — caution alerts, pending states */
  warning: 'yellow',

  /**
   * Order policy notices — the cancellation-policy alert with its acknowledgement
   * checkbox (flowers, mailed cards, gifts). Orange so it reads as distinct from the
   * yellow arrival/timing warnings shown next to it.
   */
  policyNotice: 'orange',

  /** Error/danger — error alerts, delete buttons, destructive actions */
  danger: 'red',

  /** Informational — info alerts, informational badges */
  info: 'blue',

  /** Neutral — secondary buttons, disabled states, subtle icons */
  neutral: 'gray',

  /** Feature accent — wizard steps (schedule), content generation */
  feature: 'grape',

  /** Admin/portal accent — admin navigation, portal-specific UI */
  admin: 'violet',

  /**
   * Demo account marker — the "Demo" pill on super-admin-flagged demo accounts in
   * transactional admin views (PBI #1347). Distinct from every status color so
   * simulated records are never mistaken for real ones.
   */
  demo: 'orange',

  /** Logo heart violet hex — matches the heart stroke in favicon.svg / logo-icon.svg */
  heartViolet: '#6d28d9',

  /** Accessible amber for text — use instead of amber.5/6 for body text */
  amberText: '#B45309',

  /** Indigo for display text — hero headlines, section titles with brand color */
  indigoText: '#4F46E5',

  /** Teal for display text — scheduling-related display text */
  tealText: '#0D9488',

  /**
   * Vector gift-card tile art (mailed-card order wizard).
   * Mimics CR-80 plastic gift cards: brand gradient face + metallic EMV chip.
   */
  giftCardArtMid: '#5B4FE0',
  giftCardArtEnd: '#8B5CF6',
  /** Deep corner shade for plastic depth */
  giftCardArtDeep: '#3730A3',
  /** Gold EMV chip (metallic plastic look used on retail gift cards) */
  giftCardChipLight: '#F5E6A8',
  giftCardChipMid: '#D4AF37',
  giftCardChipDark: '#A67C00',
} as const;

// ---------------------------------------------------------------------------
// Service type colors — canonical palette for each schedulable service
// ---------------------------------------------------------------------------

/**
 * Mantine color names for each service type.
 *
 * Every component that renders a service type indicator (badge, icon, chip,
 * button, ThemeIcon) MUST use these colors. Import `serviceColors` instead
 * of hardcoding 'blue' or 'pink'.
 *
 * @see ServiceTypeBadge — the canonical badge component that consumes these.
 * @see .cursor/rules/service-type-badge.mdc — enforcement rule.
 */
export const serviceColors = {
  /** E-Card service — Mantine 'blue' palette */
  eCard: 'blue',
  /** Flower ordering service — Mantine 'pink' palette */
  flowers: 'pink',
  /** Mailed card ordering service — Mantine 'grape' palette (distinct from E-Card mail icon) */
  mailedCard: 'grape',
  /** Goody gift ordering service — Mantine 'orange' palette */
  gifts: 'orange',
} as const;

// ---------------------------------------------------------------------------
// Action colors — canonical palette for interactive action buttons/icons
// ---------------------------------------------------------------------------

/**
 * Mantine color names for common user actions (buttons, ActionIcons, Menu.Items).
 *
 * Every component that renders an action button or icon MUST use these colors
 * instead of hardcoding brandColors or raw Mantine color names. This ensures
 * visual consistency across the entire application.
 *
 * Usage:
 * - `color={actionColors.edit}` on Button / ActionIcon / Menu.Item
 * - Pair with `variant="light"` for inline ActionIcons
 * - Pair with `variant="subtle"` for secondary/overflow menu items
 * - Destructive confirms use the action color with no variant (filled)
 *
 * @see .cursor/rules/action-button-colors.mdc — enforcement rule.
 */
export const actionColors = {
  /** Edit / rename — indigo (primary brand) */
  edit: 'indigo',
  /** Delete / remove / trash — red (danger) */
  delete: 'red',
  /** Preview / view / open — blue (info) */
  preview: 'blue',
  /** Send now — green (success) */
  sendNow: 'green',
  /** Cancel a pending item — red (danger, same as delete) */
  cancel: 'red',
  /** Resend a previously sent item — teal (scheduling accent) */
  resend: 'teal',
  /** Schedule / add a smile — grape (feature accent) */
  schedule: 'grape',
  /** Save / submit / confirm (non-destructive) — uses theme primary (indigo) via no color prop */
  save: 'indigo',
  /** Archive — amber (warm accent, reversible action) */
  archive: 'amber',
  /** Restore — green (success, reversal of archive) */
  restore: 'green',
  /** Settings / configure — gray (neutral) */
  settings: 'gray',
  /** Sync / exchange — teal (scheduling accent) */
  sync: 'teal',
  /** Dismiss / close (non-destructive) — no color prop needed; use variant="subtle" */
  dismiss: 'gray',
  /** Add / create — uses theme primary (indigo) via no color prop typically */
  add: 'indigo',
  /** AI / generate — violet (admin/AI accent) */
  generate: 'violet',
} as const;

// ---------------------------------------------------------------------------
// Semantic colors — for `c=` props, inline styles, and specific color needs
// ---------------------------------------------------------------------------

/**
 * Semantic color constants for text, backgrounds, borders, and overlays.
 *
 * Use these for:
 * - Mantine `c=` props (accepts both Mantine refs and hex values)
 * - Inline `style={{}}` objects
 * - Any context requiring an explicit color value
 *
 * DO NOT use these for Mantine `color=` props — use `brandColors` instead.
 */
export const semanticColors = {
  /** Text color hierarchy */
  text: {
    /** Page titles, primary headings — darkest readable tone */
    title: 'gray.9',

    /** Section headings, bold labels */
    heading: 'gray.8',

    /** Body text, descriptions */
    body: 'gray.7',

    /** Supporting copy, microcopy, timestamps — Mantine's built-in dimmed */
    secondary: 'dimmed',

    /** Subtle text — footer notes, least prominent copy */
    subtle: 'gray.6',

    /** Text on dark/colored backgrounds */
    inverse: 'white',
  },

  /** Background colors and gradients */
  bg: {
    /** Main page background — warm white, noticeably tinted */
    page: '#F8F5F0',

    /** Alternating warm section background — visual rhythm between white sections */
    sectionWarm: '#F5F3EF',

    /** Clean white section — contrasts with warm sections */
    section: '#FFFFFF',

    /** Hero background glow — soft indigo tint, anchors brand */
    heroGlow: '#E0E7FF',

    /** Navigation bar — slightly deeper indigo tint so it stands out from heroGlow / warmGradient */
    navBar: '#D0D9FC',

    /** Hero glow gradient — from indigo tint to warm white */
    heroGradient: 'linear-gradient(180deg, #E0E7FF 0%, #F8F5F0 100%)',

    /** Warm page gradient — for full-page public/landing backgrounds */
    warmGradient: 'linear-gradient(180deg, #E0E7FF 0%, #F8F5F0 30%, #FFFFFF 100%)',

    /** Accent box gradient — CTA boxes, banners (indigo-tinted, not peach) */
    accentBox: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',

    /** Card background */
    card: '#FFFFFF',

    /** Muted background — code blocks, legal sections, subtle containers */
    muted: 'gray.0',

    /** Card fallback gradient — when no template image loaded */
    cardFallback: 'linear-gradient(135deg, #8b7fc7 0%, #b09dc9 40%, #c9a5b8 100%)',

    /** Card fallback alternate gradient — lighter variant */
    cardFallbackAlt: 'linear-gradient(135deg, #f5f3ff 0%, #fce7f3 100%)',

    /** Card preview background gradient */
    cardPreview: 'linear-gradient(135deg, #fce4ec 0%, #e8eaf6 50%, #e3f2fd 100%)',

    /** Card footer/header muted background */
    cardMuted: '#fafafa',

    /** Filter bar background — subtle cool-gray tint to distinguish filter UI from content */
    filterBar: '#F8F9FC',

    /**
     * Soft success callout — free / member-covered confirmation panels
     * (mint gradient so the Free badge and copy read as positive savings).
     */
    successSoft: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',

    /**
     * Provisional / estimated smile order cards on Orders (PBI #1198).
     * Cool gray fill so dashed "not final" rows read clearly against the
     * warm page gradient and solid white charged cards.
     */
    estimatedOrder: '#EEF0F4',
  },

  /** Border colors */
  border: {
    /** Standard borders — clean slate */
    default: '#E2E8F0',

    /** Accent borders — indigo-tinted for CTA boxes */
    accent: '#C7D2FE',

    /**
     * Dashed outline for estimated (not-yet-charged) Orders rows.
     * Stronger than {@link default} so provisional cards don't dissolve into the page.
     */
    estimatedOrder: '#94A3B8',

    /** Soft success border — pairs with bg.successSoft */
    success: '#6EE7B7',

    /** Subtle dividers between list items */
    subtle: 'gray.2',

    /** Card footer/header border */
    cardMuted: '#eeeeee',

    /** Card fallback border */
    cardFallback: '2px solid rgba(124, 58, 237, 0.12)',
  },

  /** Overlay and shadow colors — for cards, modals, and floating elements */
  overlay: {
    /** Light semi-transparent white — card overlays, frosted glass */
    light: 'rgba(255, 255, 255, 0.9)',

    /** Medium semi-transparent white — cover overlays */
    medium: 'rgba(255, 255, 255, 0.45)',

    /** Cover card overlay */
    coverCard: 'rgba(255, 255, 255, 0.25)',

    /** Cover border overlay */
    coverBorder: 'rgba(255, 255, 255, 0.3)',

    /** Animation cover overlay */
    animationCover: 'rgba(255, 255, 255, 0.7)',

    /** Subtle dark overlay — divider lines within cards */
    darkSubtle: 'rgba(0, 0, 0, 0.05)',

    /** Standard shadow */
    shadow: 'rgba(0, 0, 0, 0.08)',

    /** Deeper shadow — modals, elevated cards */
    shadowDeep: 'rgba(0, 0, 0, 0.12)',

    /** Lightest shadow — hover states */
    shadowLight: 'rgba(0, 0, 0, 0.04)',

    /** Text shadow for text on images */
    textShadow: 'rgba(0,0,0,0.2)',

    /** Lighter text shadow */
    textShadowLight: 'rgba(0,0,0,0.15)',
  },

  /** Card display text color — for text rendered inside card content */
  cardText: '#333',

  /** Wizard step backgrounds and borders */
  wizard: {
    /** Schedule wizard — grape-tinted */
    schedule: { bg: 'linear-gradient(135deg, #f9f0ff 0%, #fff0f6 100%)', border: '#d3adf7' },

    /** Event wizard — teal-tinted */
    event: { bg: 'linear-gradient(135deg, #e6f7ff 0%, #e6fffb 100%)', border: '#87e8de' },

    /** Contact wizard — warm-tinted */
    contact: { bg: 'linear-gradient(135deg, #fff5f5 0%, #fff0e6 100%)', border: '#ffc9c9' },

    /** Contact delete button gradient */
    contactDelete: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)',
  },

  /**
   * Error states for invalid form inputs.
   *
   * Used both as Mantine references (for `c=` props) and as a single source of
   * truth that mirrors the `--ss-error-*` CSS variables in `src/style.css`. The
   * actual visual treatment (soft pink background, thicker red border, red
   * helper text) is applied globally via CSS targeting Mantine's stable
   * `[data-error]` data attribute — see the "Form validation error visuals"
   * block in `src/style.css`. We keep the styling in CSS rather than the
   * Mantine `theme.components.Input.styles` API because Mantine's inline
   * `styles` API produces inline `style` attributes and cannot express
   * nested selectors like `&[data-error="true"]`.
   *
   * @see .cursor/rules/form-validation.mdc
   */
  error: {
    /** Soft pink wash for the input background (Mantine `red.0`). */
    bg: 'var(--mantine-color-red-0)',
    /** Strong red border (Mantine `red.6`). */
    border: 'var(--mantine-color-red-6)',
    /** Red helper text below the input (Mantine `red.7`, ≈ 7.4:1 on white — passes WCAG AA). */
    text: 'var(--mantine-color-red-7)',
  },

  /**
   * Muted gift-card tile ("None") — SVG fills when the customer declines an add-on.
   * Kept as concrete hex because SVG stopColor cannot use Mantine CSS variables reliably.
   */
  giftCardArtMuted: {
    start: '#F3F4F6',
    end: '#E5E7EB',
    deep: '#D1D5DB',
    stroke: '#9CA3AF',
    chipLight: '#E5E7EB',
    chipMid: '#D1D5DB',
    chipDark: '#9CA3AF',
    label: '#6B7280',
    wordmark: '#9CA3AF',
  },
} as const;

// ---------------------------------------------------------------------------
// Common style objects — reusable across components
// ---------------------------------------------------------------------------

/**
 * Common style objects for reuse in components.
 */
export const commonStyles = {
  /** Accent/highlight box — CTA boxes, banners */
  accentBox: {
    background: semanticColors.bg.accentBox,
    border: `1px solid ${semanticColors.border.accent}`,
  },

  /** Standard card with clean border */
  warmCard: {
    borderColor: semanticColors.border.default,
  },

  /** Full-page warm background (uses dvh for mobile-safe viewport height) */
  warmPageBackground: {
    minHeight: 'var(--app-vh, 100vh)',
    background: semanticColors.bg.warmGradient,
  },
} as const;

// ---------------------------------------------------------------------------
// Mantine theme configuration
// ---------------------------------------------------------------------------

/**
 * Mantine theme configuration.
 *
 * Overrides built-in indigo and teal palettes with custom Tailwind-based scales.
 * Adds amber as a new palette for warm accents.
 *
 * Light-variant appearance: {@link App} wires `appCssVariablesResolver` on
 * MantineProvider to preserve v8 transparent `variant="light"` colors. Follow-up
 * work item: drop the resolver and adopt Mantine 9 solid light variants (more
 * accessible) once visual QA completes.
 */
export const appTheme = createTheme({
  primaryColor: brandColors.primary,

  /** Custom color scales */
  colors: {
    indigo: ssIndigo,
    teal: ssTeal,
    amber: ssAmber,
  },

  /** Default border radius for components */
  defaultRadius: 'md',

  /** Font configuration */
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',

  /** Heading styles */
  headings: {
    fontWeight: '700',
  },

  /**
   * Mantine 9 defaults medium font-weight to 600; we keep 500 to match v8
   * typography (nav labels, table row titles, ~147 explicit fw={500} sites).
   */
  fontWeights: {
    medium: '500',
  },

  /** Component-specific overrides */
  components: {
    /** Modal titles are bold for visual hierarchy */
    Modal: {
      styles: {
        title: {
          fontWeight: 700,
        },
      },
    },

    // Mantine 9 ships an updated Floating UI stack (mantinedev/mantine#8038 area).
    // Theme-level `withinPortal: false` / `floatingStrategy: 'fixed'` defaults were
    // removed during the v9 upgrade — re-validate in-modal Select/Date dropdowns in
    // Playwright. Call sites that need the workaround pass popoverProps/comboboxProps
    // explicitly (PhoneNumberInput flag picker; AI Writing Assistant More tones uses
    // floatingStrategy: 'fixed' so the menu cannot widen a phone-width modal, #1378).

    /**
     * Input: Prevent iOS Safari auto-zoom on focus.
     *
     * iOS Safari auto-zooms when a user taps any input with font-size below 16px.
     * Mantine's default "sm" size is ~14px, which triggers the zoom. Once zoomed,
     * the page becomes wider than the viewport, modal buttons get pushed off-screen,
     * and the layout becomes inconsistent. Setting the input font-size to 1rem (16px)
     * prevents this without disabling pinch-to-zoom (which would hurt accessibility).
     *
     * Error-state styling (soft pink background, thicker red border, red helper
     * text) is applied via global CSS in `src/style.css` (see "Form validation
     * error visuals"). Mantine's inline `styles` API can't express the
     * nested `[data-error]` selector cleanly, so the override lives in CSS
     * targeting Mantine's stable static class names + data attributes.
     */
    Input: {
      styles: {
        input: {
          fontSize: '1rem',
        },
      },
    },

    /**
     * Form field subheaders: slightly larger than Mantine's default (sm) so
     * labels in modals (TextInput, Textarea, `Input.Label`) read as clear section
     * titles. Descriptions use body-toned text instead of the dimmed microcopy
     * default, so help lines like "Edit to personalize ..." stay readable.
     */
    InputLabel: {
      defaultProps: {
        size: 'md',
      },
    },
    InputDescription: {
      defaultProps: {
        size: 'sm',
      },
      styles: {
        description: {
          // Stronger than `c="dimmed"` / gray.6 — aligns with `semanticColors.text.body`
          color: 'var(--mantine-color-gray-7)',
        },
      },
    },

    /** Paper components use rounded corners by default */
    Paper: {
      defaultProps: {
        radius: 'lg',
      },
    },

    /** Buttons use primary brand color by default */
    Button: {
      defaultProps: {
        color: brandColors.primary,
      },
    },

    /** ThemeIcons use primary brand color by default */
    ThemeIcon: {
      defaultProps: {
        color: brandColors.primary,
      },
    },

    /** Dividers use subtle border color */
    Divider: {
      defaultProps: {
        color: semanticColors.border.subtle,
      },
    },
  },
});

// ---------------------------------------------------------------------------
// Logo sizes
// ---------------------------------------------------------------------------

/**
 * Logo size constants.
 */
export const logoSizes = {
  /** Small logo (header, nav) */
  small: rem(32),

  /** Medium logo (landing page header) */
  medium: rem(36),

  /** Large logo (splash screens) */
  large: rem(48),
} as const;

// ---------------------------------------------------------------------------
// Brand font families — imported via Google Fonts in index.html
// ---------------------------------------------------------------------------

/**
 * Named font families for specific use cases beyond the default system-ui stack.
 * Each font is loaded via Google Fonts in index.html.
 */
export const brandFonts = {
  /** Friendly rounded sans-serif — hero headlines, large display text */
  headline: "'Nunito', system-ui, sans-serif",

  /** Handwritten accent — decorative hook lines, personal touches */
  handwritten: "'Caveat', cursive",

  /** Classic serif — display rotator words, editorial emphasis */
  serif: "'DM Serif Display', serif",
} as const;

// ---------------------------------------------------------------------------
// Holiday backgrounds
// ---------------------------------------------------------------------------

/**
 * Holiday background configuration.
 *
 * To change the landing page background for a holiday:
 * 1. Add the SVG to /public/backgrounds/
 * 2. Update `currentHolidayBackground` below
 * 3. Set to null for no holiday background
 */
export const holidayBackgrounds = {
  valentines: '/backgrounds/valentines.svg',
} as const;

/**
 * Current holiday background to display on the landing page.
 * Set to null for no holiday theming.
 */
export const currentHolidayBackground: string | null = holidayBackgrounds.valentines;
